import * as React from 'react';
import { TShippingForm } from './ShippingForm.types';
import './ShippingForm.styled.scss';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField, Button, Card, CardContent, Divider } from '@mui/material';
import { TShippingFormData } from '../../Checkout.types';
import {
  DELIVERY_METHODS,
  SHIPPING_FORM_DEFAULT_VALUES,
} from '../../Checkout.constants';
import { getShippingSchema } from './ShippingForm.schema';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import {
  MAX_DEFAULT_BIG_FIELD_SYMBOLS,
  MAX_DEFAULT_FIELD_SYMBOLS,
} from 'constants/general';

const SHIPPING_DEFAULT_FIELD_INPUT_MAX = MAX_DEFAULT_FIELD_SYMBOLS + 1;
const SHIPPING_ADDITIONAL_INFO_INPUT_MAX = MAX_DEFAULT_BIG_FIELD_SYMBOLS + 1;

export const ShippingForm: TShippingForm = ({
  initialData,
  onContinue,
  onBack,
}) => {
  const localize = useLocalize();
  const shippingSchema = React.useMemo(
    () => getShippingSchema(localize),
    [localize]
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TShippingFormData>({
    resolver: yupResolver(shippingSchema) as Resolver<TShippingFormData>,
    defaultValues: initialData || SHIPPING_FORM_DEFAULT_VALUES,
  });

  const selectedDeliveryMethod = watch('deliveryMethod');
  const inPostDeliveryMethod = DELIVERY_METHODS[0];

  const onSubmit = (data: TShippingFormData) => {
    onContinue(data as TShippingFormData);
  };

  return (
    <div className="shipping-form">
      <div className="shipping-form__header">
        <h6 className="shipping-form__title">
          {localize('checkout.shipping.title')}
        </h6>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="shipping-form__form"
        noValidate
      >
        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">
            {localize('checkout.shipping.contact.section')}
          </h6>
          <div className="shipping-form__row">
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.first.name') as string}
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.last.name') as string}
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('general.label.email') as string}
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.phone') as string}
                  fullWidth
                  error={!!errors.phone}
                  helperText={
                    errors.phone?.message ||
                    (localize('checkout.shipping.phone.helper') as string)
                  }
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">
            {localize('checkout.shipping.address.section')}
          </h6>
          <div className="shipping-form__row">
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.street') as string}
                  fullWidth
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
            <Controller
              name="buildingNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={
                    localize('checkout.shipping.building.number') as string
                  }
                  fullWidth
                  error={!!errors.buildingNumber}
                  helperText={errors.buildingNumber?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
            <Controller
              name="apartmentNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={
                    localize('checkout.shipping.apartment.optional') as string
                  }
                  fullWidth
                  error={!!errors.apartmentNumber}
                  helperText={errors.apartmentNumber?.message}
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
          </div>
          <div className="shipping-form__row">
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.city') as string}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={localize('checkout.shipping.postal.code') as string}
                  fullWidth
                  error={!!errors.postalCode}
                  helperText={
                    errors.postalCode?.message ||
                    (localize('checkout.shipping.postal.helper') as string)
                  }
                  required
                  inputProps={{ maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX }}
                />
              )}
            />
          </div>
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">
            {localize('checkout.shipping.additional.info.section')}
          </h6>
          <Controller
            name="additionalInfo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  localize('checkout.shipping.additional.info.label') as string
                }
                placeholder={
                  localize(
                    'checkout.shipping.additional.info.placeholder'
                  ) as string
                }
                multiline
                minRows={4}
                fullWidth
                inputProps={{ maxLength: SHIPPING_ADDITIONAL_INFO_INPUT_MAX }}
                error={!!errors.additionalInfo}
                helperText={errors.additionalInfo?.message}
              />
            )}
          />
        </div>

        <Divider className="shipping-form__divider" />

        <div className="shipping-form__section">
          <h6 className="shipping-form__section-title">
            {localize('checkout.shipping.delivery.section')}
          </h6>
          <Card className="shipping-form__delivery-card shipping-form__delivery-card--selected">
            <CardContent>
              <div className="shipping-form__delivery-info">
                <div className="shipping-form__delivery-header">
                  <h6>
                    {localize(
                      `checkout.delivery.${inPostDeliveryMethod.id}.name`
                    )}
                  </h6>
                  <h6>
                    {inPostDeliveryMethod.price === 0
                      ? localize('checkout.shipping.price.free')
                      : localize('checkout.shipping.price.zloty', [
                          String(inPostDeliveryMethod.price),
                        ])}
                  </h6>
                </div>
                <p className="body2">
                  {localize(
                    `checkout.delivery.${inPostDeliveryMethod.id}.description`
                  )}
                </p>
                <p className="caption">
                  {localize('checkout.shipping.estimated.prefix')}{' '}
                  {localize(`checkout.delivery.${inPostDeliveryMethod.id}.eta`)}
                </p>
              </div>
            </CardContent>
          </Card>
          {errors.deliveryMethod ? (
            <p className="caption">{errors.deliveryMethod.message}</p>
          ) : null}

          {selectedDeliveryMethod === 'inpost' ? (
            <div className="shipping-form__inpost-section">
              <Controller
                name="parcelLockerCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={
                      localize(
                        'checkout.shipping.parcel.locker.code.label'
                      ) as string
                    }
                    placeholder={
                      localize(
                        'checkout.shipping.parcel.locker.code.placeholder'
                      ) as string
                    }
                    fullWidth
                    required
                    error={!!errors.parcelLockerCode}
                    helperText={errors.parcelLockerCode?.message}
                    inputProps={{
                      maxLength: SHIPPING_DEFAULT_FIELD_INPUT_MAX,
                    }}
                  />
                )}
              />
            </div>
          ) : null}
        </div>

        <div className="shipping-form__actions">
          <Button
            variant="outlined"
            onClick={onBack}
            className="shipping-form__back-button"
          >
            {localize('checkout.shipping.button.back')}
          </Button>
          <Button type="submit" variant="contained">
            {localize('checkout.shipping.button.continue.payment')}
          </Button>
        </div>
      </form>
    </div>
  );
};
